import { useMemo } from "react";
import { useViewer } from "@/features/profile/hooks/useViewer";
import {
  PermissionAdministration,
  PermissionAdministrationConstraints,
  PersonRole,
} from "@/features/auth/graphql/generated";
import { getPersonRoleObject } from "@/shared/utils/selectors/getRolesAndAccess";
import { ResourceEnum } from "@/shared/constants/resources";

type IntersectOrFallbackParams<T extends string> = {
  backendList?: (T | null | undefined)[] | null;
  fallbackList?: (T | null | undefined)[] | null;
};

export const intersectOrFallback = <T extends string>({
  backendList,
  fallbackList,
}: IntersectOrFallbackParams<T>): T[] => {
  const backend = (backendList || []).filter(Boolean) as T[];
  const fallback = (fallbackList || []).filter(Boolean) as T[];

  if (backend.length === 0) return fallback;
  if (fallback.length === 0) return backend;

  const fallbackSet = new Set(fallback);
  return backend.filter((v) => fallbackSet.has(v));
};

export type UsePermissionAdministrationParams = {
  resourceType: ResourceEnum;
  resourceId: number;
};

export type PermissionAdministrationAdapter = {
  adminRole?: PersonRole;
  administration?: PermissionAdministration;
  constraints?: PermissionAdministrationConstraints;
  notes: string[];
  canInvite: boolean;
  canChangeRole: boolean;
  canChangeAccessLevel: boolean;
  canRevoke: boolean;
  grantableRoles: string[];
  grantableAccessLevels: string[];
};

/**
 * Adapter hook over backend-provided `role.administration` contract.
 *
 * This is the source of truth for "Manage People" actions (invite/change role/access/revoke).
 * Fail-closed: when administration data is missing, it returns `can* = false` and empty grantables.
 */
export const usePermissionAdministration = ({
  resourceType,
  resourceId,
}: UsePermissionAdministrationParams): PermissionAdministrationAdapter => {
  const { viewer } = useViewer();

  const roles = useMemo(
    () => ((viewer.person?.roles || []) as PersonRole[]).filter(Boolean),
    [viewer.person?.roles]
  );

  const adminRole = useMemo(() => {
    if (!resourceId) return undefined;
    return getPersonRoleObject(roles, resourceType, resourceId);
  }, [roles, resourceType, resourceId]);

  const administration = adminRole?.administration || undefined;
  const constraints = administration?.constraints || undefined;

  const grantableRoles = (administration?.grantable_roles || []).filter(
    Boolean
  ) as string[];
  const grantableAccessLevels = (
    administration?.grantable_access_levels || []
  ).filter(Boolean) as string[];

  const notes = (constraints?.notes || []).filter(Boolean) as string[];

  return {
    adminRole,
    administration,
    constraints,
    notes,
    canInvite: administration?.can_invite === true,
    canChangeRole: administration?.can_change_role === true,
    canChangeAccessLevel: administration?.can_change_access_level === true,
    canRevoke: administration?.can_revoke === true,
    grantableRoles,
    grantableAccessLevels,
  };
};
